import React, { useEffect } from 'react';
import HelpLayout from '../../../components/help/HelpLayout';
import {
  ChartBarIcon,
  HomeIcon,
  BellIcon,
  UserIcon,
  AcademicCapIcon,
  QuestionMarkCircleIcon,
  ArrowRightOnRectangleIcon,
  InformationCircleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const DashboardHelp = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { name: 'User Guide', href: '/help/getting-started' },
    { name: 'Dashboard Navigation' }
  ];

  const navigationFeatures = [
    {
      title: 'Improved Navigation Bar',
      icon: HomeIcon,
      description: 'Enhanced navigation with better spacing and professional appearance',
      features: [
        'Optimized button spacing for better visual hierarchy',
        'Consistent 44px height for all navigation elements',
        'Improved gaps between navigation sections',
        'Professional appearance across all screen sizes'
      ]
    },
    {
      title: 'User Welcome Section',
      icon: UserIcon,
      description: 'Personalized welcome message with double-story layout',
      features: [
        'Two-line welcome message: "Welcome," and your name',
        'Profile picture with verification badge',
        'Clean, professional appearance',
        'Better use of navigation space'
      ]
    },
    {
      title: 'Notifications System',
      icon: BellIcon,
      description: 'Stay informed with the enhanced notifications bell',
      features: [
        'Red badge shows unread announcement count',
        'Click to open notifications modal',
        'Real-time updates for new announcements',
        'Mobile-friendly design'
      ]
    },
    {
      title: 'Help & Support',
      icon: QuestionMarkCircleIcon,
      description: 'Easy access to help documentation and support',
      features: [
        'Question mark icon for quick help access',
        'Comprehensive user and admin documentation',
        'Context-sensitive help sections',
        'Contact support options'
      ]
    }
  ];

  const dashboardSections = [
    {
      title: 'Course Registrations',
      description: 'View and manage your active course applications and registrations',
      features: ['Application status tracking', 'Course details and schedules', 'Progress monitoring']
    },
    {
      title: 'Opportunity Applications',
      description: 'Track your job, apprenticeship, and internship applications',
      features: ['Application status updates', 'Interview scheduling', 'Employer communications']
    },
    {
      title: 'Career Recommendations',
      description: 'Personalized course and opportunity suggestions based on your assessment',
      features: ['Skill-matched recommendations', 'Career pathway guidance', 'Updated suggestions']
    },
    {
      title: 'Announcements',
      description: 'Important updates and notifications from GCRA',
      features: ['System updates', 'Course deadlines', 'Job opportunities', 'Training announcements']
    },
    {
      title: 'Wishlist',
      description: 'Courses and opportunities you\'ve saved for future consideration',
      features: ['Saved courses and jobs', 'Quick application access', 'Deadline reminders']
    },
    {
      title: 'Profile Completion',
      description: 'Track your profile completion status',
      features: ['Required fields checklist', 'Completion percentage', 'Quick edit access']
    }
  ];

  return (
    <HelpLayout title="Dashboard & Navigation" breadcrumbs={breadcrumbs}>
      <div className="help-content">
        <div className="card">
          <div className="card-body">
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px'
            }}>
              Dashboard & Navigation Guide
            </h2>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              lineHeight: 1.6,
              marginBottom: '32px'
            }}>
              Navigate your dashboard effectively and track your training progress, applications, and announcements.
              Learn about the improved navigation system and dashboard features.
            </p>

            {/* New Features Highlight */}
            <div style={{
              background: 'var(--primary-yellow-light)',
              border: '2px solid var(--primary-yellow)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '32px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: 'var(--primary-black)',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <CheckCircleIcon style={{ width: '20px', height: '20px' }} />
                New: Enhanced Login Experience
              </h3>
              <p style={{
                fontSize: '14px',
                color: 'var(--primary-black)',
                margin: 0,
                lineHeight: 1.5
              }}>
                After logging in, you'll now be automatically redirected to your dashboard instead of the home page.
                This gets you straight to your personalized content and course information faster!
              </p>
            </div>

            {/* Navigation Features */}
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: 'var(--primary-black)',
              marginBottom: '20px'
            }}>
              Navigation Features
            </h3>

            {navigationFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} style={{
                  border: '1px solid var(--border-gray)',
                  borderRadius: '12px',
                  marginBottom: '20px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    background: 'var(--background-gray)',
                    padding: '16px 20px',
                    borderBottom: '1px solid var(--border-gray)'
                  }}>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Icon style={{ width: '18px', height: '18px', color: 'var(--primary-yellow)' }} />
                      {feature.title}
                    </h4>
                  </div>
                  <div style={{ padding: '16px 20px' }}>
                    <p style={{
                      fontSize: '14px',
                      color: 'var(--text-gray)',
                      marginBottom: '12px',
                      lineHeight: 1.5
                    }}>
                      {feature.description}
                    </p>
                    <ul style={{
                      margin: 0,
                      paddingLeft: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px'
                    }}>
                      {feature.features.map((item, itemIndex) => (
                        <li key={itemIndex} style={{
                          fontSize: '13px',
                          color: 'var(--text-dark)',
                          lineHeight: 1.4
                        }}>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}

            {/* Dashboard Sections */}
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: 'var(--primary-black)',
              marginBottom: '20px',
              marginTop: '32px'
            }}>
              Dashboard Sections
            </h3>

            <div style={{
              display: 'grid',
              gap: '16px',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              marginBottom: '32px'
            }}>
              {dashboardSections.map((section, index) => (
                <div key={index} style={{
                  border: '1px solid var(--border-gray)',
                  borderRadius: '8px',
                  padding: '16px',
                  background: 'var(--white)'
                }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: 'var(--primary-black)',
                    marginBottom: '8px'
                  }}>
                    {section.title}
                  </h4>
                  <p style={{
                    fontSize: '13px',
                    color: 'var(--text-gray)',
                    marginBottom: '12px',
                    lineHeight: 1.4
                  }}>
                    {section.description}
                  </p>
                  <ul style={{
                    margin: 0,
                    paddingLeft: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    {section.features.map((feature, featureIndex) => (
                      <li key={featureIndex} style={{
                        fontSize: '12px',
                        color: 'var(--text-dark)',
                        lineHeight: 1.3
                      }}>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Tips Section */}
            <div style={{
              background: '#F0F9FF',
              border: '1px solid #BAE6FD',
              borderRadius: '12px',
              padding: '20px',
              marginTop: '32px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#0C4A6E',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <InformationCircleIcon style={{ width: '20px', height: '20px' }} />
                Navigation Tips
              </h3>
              <div style={{
                display: 'grid',
                gap: '12px',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))'
              }}>
                <div>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#0C4A6E',
                    marginBottom: '6px'
                  }}>
                    Quick Access
                  </h4>
                  <p style={{
                    fontSize: '13px',
                    color: '#374151',
                    margin: 0,
                    lineHeight: 1.4
                  }}>
                    Use the navigation bar to quickly jump between Dashboard, Profile, Courses, and Help sections.
                  </p>
                </div>
                <div>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#0C4A6E',
                    marginBottom: '6px'
                  }}>
                    Stay Updated
                  </h4>
                  <p style={{
                    fontSize: '13px',
                    color: '#374151',
                    margin: 0,
                    lineHeight: 1.4
                  }}>
                    Check the notifications bell regularly for important announcements and course updates.
                  </p>
                </div>
                <div>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#0C4A6E',
                    marginBottom: '6px'
                  }}>
                    Mobile Friendly
                  </h4>
                  <p style={{
                    fontSize: '13px',
                    color: '#374151',
                    margin: 0,
                    lineHeight: 1.4
                  }}>
                    The navigation works seamlessly on mobile devices with touch-friendly buttons and responsive design.
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Action */}
            <div style={{
              background: 'var(--background-gray)',
              borderRadius: '12px',
              padding: '24px',
              marginTop: '32px',
              textAlign: 'center'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                Ready to Explore Your Dashboard?
              </h3>
              <p style={{
                fontSize: '14px',
                color: 'var(--text-gray)',
                marginBottom: '20px'
              }}>
                Visit your dashboard to see your course registrations, announcements, and track your progress.
              </p>
              <a
                href="/dashboard"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'var(--primary-yellow)',
                  color: 'var(--primary-black)',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(255, 193, 7, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <ChartBarIcon style={{ width: '16px', height: '16px' }} />
                Go to Dashboard
              </a>
            </div>
          </div>
        </div>
      </div>
    </HelpLayout>
  );
};

export default DashboardHelp;
